"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GridInternalApi = void 0;
const tslib_1 = require("tslib");
const ApiBase_1 = require("../Implementation/ApiBase");
const Enums_1 = require("../../PredefinedConfig/Common/Enums");
const ArrayExtensions_1 = tslib_1.__importDefault(require("../../Utilities/Extensions/ArrayExtensions"));
const GridRedux = tslib_1.__importStar(require("../../Redux/ActionsReducers/GridRedux"));
const NumberExtensions_1 = tslib_1.__importDefault(require("../../Utilities/Extensions/NumberExtensions"));
class GridInternalApi extends ApiBase_1.ApiBase {
    /**
     * Fires Grid Sorted Event
     */
    fireGridSortedEvent() {
        if (this.adaptable.isLive) {
            const adaptableSortState = this.adaptable.api.configApi.getAdaptableSortState();
            const adaptableApi = this.getAdaptableApi();
            const gridSortedInfo = {
                adaptableSortState: adaptableSortState,
                adaptableApi: adaptableApi,
                userName: adaptableApi.optionsApi.getUserName(),
                adaptableId: adaptableApi.optionsApi.getAdaptableId(),
            };
            adaptableApi.eventApi.emit('GridSorted', gridSortedInfo);
        }
    }
    /**
     * Fires Cell Changed Changed Event - when any data in AG Grid's dataset has changed
     */
    fireCellChangedEvent(cellDataChangedInfo) {
        if (this.adaptable.isLive) {
            const cellChangedInfo = {
                adaptableApi: this.adaptable.api,
                cellChange: cellDataChangedInfo,
                userName: this.adaptable.api.optionsApi.getUserName(),
                adaptableId: this.adaptable.api.optionsApi.getAdaptableId(),
            };
            this.adaptable.api.eventApi.emit('CellChanged', cellChangedInfo);
        }
    }
    /**
     * Fires Grid Data Changed Event - when a row has changed in AG Grid
     */
    fireGridDataChangedEvent(dataRows, rowNodes, rowTrigger) {
        if (this.adaptable.isLive) {
            const gridDataChangedInfo = {
                changedAt: Date.now(),
                adaptableApi: this.adaptable.api,
                rowTrigger,
                dataRows,
                rowNodes,
                userName: this.adaptable.api.optionsApi.getUserName(),
                adaptableId: this.adaptable.api.optionsApi.getAdaptableId(),
            };
            this.adaptable.api.eventApi.emit('GridDataChanged', gridDataChangedInfo);
            this.adaptable.DataService.CreateGridChangedEvent(gridDataChangedInfo);
        }
    }
    /**
     * Gets all distinct display values in the Column with the given ColumnId
     * @param columnId Column to check
     */
    getDistinctDisplayValuesForColumn(columnId) {
        const abColumn = this.getColumnApi().getColumnWithColumnId(columnId);
        if (abColumn == undefined) {
            return [];
        }
        const distinctValuesParams = {
            visibleRowsOnly: false,
        };
        const returnValues = this.adaptable.getDistinctValuesForColumn(abColumn, distinctValuesParams);
        return this.sortDistinctValues(returnValues, abColumn).map((cv) => {
            return cv.normalisedValue;
        });
    }
    /**
     * Gets all distinct Filter values for the Column with the given ColumnId (used for Floating Filter and Column Header filter)
     * @param columnId Column to check
     * @param columnFilter Current applied filter
     */
    async getDistinctFilterDisplayValuesForColumn(columnId, filter, showFilteredRowsOnly) {
        const abColumn = this.getColumnApi().getColumnWithColumnId(columnId);
        if (abColumn == undefined) {
            return {
                values: [],
                suppressClientSideFilter: false,
            };
        }
        const distinctValuesParams = {
            visibleRowsOnly: showFilteredRowsOnly,
        };
        const { gridCells, suppressClientSideFilter } = await this.adaptable.getDistinctFilterListValuesForColumn(abColumn, filter, distinctValuesParams);
        let showValuesCount = false;
        const showValuesCountFunction = this.getColumnFilterOptions().valuesFilterOptions.showValuesCount;
        if (showValuesCountFunction) {
            showValuesCount = showValuesCountFunction(abColumn);
        }
        return {
            values: this.sortDistinctValues(gridCells, abColumn).map((cv) => {
                const label = showValuesCount
                    ? cv.displayValue +
                        NumberExtensions_1.default.WrapInParentheses(this.getGridApi().getCellDisplayValueCount(abColumn.columnId, cv.displayValue))
                    : cv.displayValue;
                return {
                    label: label,
                    value: cv.normalisedValue,
                };
            }),
            suppressClientSideFilter,
        };
    }
    /**
     * Gets all distinct Custom Sort values for the Column with the given ColumnId
     * @param columnId Column to check
     */
    async getDistinctCustomSortDisplayValuesForColumn(columnId) {
        const abColumn = this.getColumnApi().getColumnWithColumnId(columnId);
        if (abColumn == undefined) {
            return [];
        }
        const distinctValuesParams = {
            visibleRowsOnly: false,
        };
        const gridCells = await this.adaptable.getDistinctCustomSortValuesForColumn(abColumn, distinctValuesParams);
        return this.sortDistinctValues(gridCells, abColumn).map((cv) => {
            return cv.normalisedValue;
        });
    }
    /**
     * Gets all distinct Bulk Update values for the Column with the given ColumnId
     * @param columnId Column to check
     * @param selectedGridCells Selected grid cells
     */
    async getDistinctBulkUpdateDisplayValuesForColumn(columnId, selectedGridCells) {
        const abColumn = this.getColumnApi().getColumnWithColumnId(columnId);
        if (abColumn == undefined) {
            return [];
        }
        const distinctValuesParams = {
            visibleRowsOnly: false,
        };
        const gridCells = await this.adaptable.getDistinctBulkUpdateValuesForColumn(abColumn, selectedGridCells, distinctValuesParams);
        return this.sortDistinctValues(gridCells, abColumn).map((cv) => {
            return cv.normalisedValue;
        });
    }
    /**
     * Gets all distinct visible (after current filters are applied) display values in the Column with the given ColumnId
     * @param columnId Column to check
     */
    getDistinctVisibleDisplayValuesForColumn(columnId) {
        const abColumn = this.getColumnApi().getColumnWithColumnId(columnId);
        if (abColumn == undefined) {
            return [];
        }
        const distinctValuesParams = {
            visibleRowsOnly: true,
        };
        const returnValues = this.adaptable.getDistinctValuesForColumn(abColumn, distinctValuesParams);
        return this.sortDistinctValues(returnValues, abColumn).map((cv) => {
            return cv.normalisedValue;
        });
    }
    /**
     * Gets all distinct raw values in Column. Values are sorted.
     * @param columnId Column to check
     * @param skipRowNode optional RowNode which should be skipped when collecting the distinct rawValues
     */
    getDistinctRawValuesForColumn(columnId, skipRowNode) {
        const abColumn = this.getColumnApi().getColumnWithColumnId(columnId);
        if (abColumn == undefined) {
            return [];
        }
        return this.sortDistinctValues(this.getUnsortedDistinctRawValuesForColumn(columnId, skipRowNode), abColumn).map((cv) => {
            return cv.rawValue;
        });
    }
    /**
     * Gets all distinct raw values in Column. Values are un-sorted.
     * @param columnId Column to check
     * @param skipRowNode optional RowNode which should be skipped when collecting the distinct rawValues
     */
    getUnsortedDistinctRawValuesForColumn(columnId, skipRowNode) {
        const abColumn = this.getColumnApi().getColumnWithColumnId(columnId);
        if (abColumn == undefined) {
            return [];
        }
        const distinctValuesParams = {
            visibleRowsOnly: false,
            skipRowNode: skipRowNode,
        };
        return this.adaptable.getDistinctValuesForColumn(abColumn, distinctValuesParams);
    }
    /**
     * Gets all distinct visible raw values in the Column
     * @param columnId Column to check
     */
    getDistinctVisibleRawValuesForColumn(columnId) {
        const abColumn = this.getColumnApi().getColumnWithColumnId(columnId);
        if (abColumn == undefined) {
            return [];
        }
        const distinctValuesParams = {
            visibleRowsOnly: true,
        };
        const returnValues = this.adaptable.getDistinctValuesForColumn(abColumn, distinctValuesParams);
        return this.sortDistinctValues(returnValues, abColumn).map((cv) => {
            return cv.rawValue;
        });
    }
    sortDistinctValues(returnValues, column) {
        // this does NOT into account Custom Sort - far too hard for now...
        let sortOrder = Enums_1.SortOrder.Asc;
        if (this.getColumnFilterOptions().valuesFilterOptions.sortValuesFilter) {
            let columnSort = this.adaptable.api.gridApi.getColumnSortForColumn(column.columnId);
            if (columnSort && columnSort.SortOrder == 'Desc') {
                sortOrder = Enums_1.SortOrder.Desc;
            }
        }
        if (this.getColumnApi().hasNumberDataType(column === null || column === void 0 ? void 0 : column.columnId)) {
            returnValues = ArrayExtensions_1.default.sortCellValueArrayNumeric(returnValues, sortOrder);
        }
        else if (this.getColumnApi().hasDateDataType(column === null || column === void 0 ? void 0 : column.columnId)) {
            returnValues = ArrayExtensions_1.default.sortCellValueArrayDates(returnValues, sortOrder);
        }
        else {
            returnValues = ArrayExtensions_1.default.sortCellValueArray(returnValues, sortOrder);
        }
        return returnValues;
    }
    setColumns(columns) {
        this.dispatchAction(GridRedux.GridSetColumns(columns));
    }
    setSelectedCells(selectedCellInfo) {
        this.dispatchAction(GridRedux.GridSetSelectedCells(selectedCellInfo));
    }
    setSelectedRows(selectedRowInfo) {
        this.dispatchAction(GridRedux.GridSetSelectedRows(selectedRowInfo));
    }
    buildStandaloneColumnHeader(column) {
        return this.adaptable.buildStandaloneColumnHeader(column);
    }
    getEditLookUpValuesForColumn(editLookUpItem, column, gridCell) {
        if (!editLookUpItem || !column) {
            return undefined;
        }
        let editLookUpValues = editLookUpItem.values;
        // first do the function then get hardcoded items
        if (editLookUpValues != null && typeof editLookUpValues === 'function') {
            const editLookUpContext = {
                adaptableApi: this.adaptable.api,
                userName: this.getOptions().userName,
                adaptableId: this.getOptions().adaptableId,
                column: column,
                gridCell,
            };
            return editLookUpValues(editLookUpContext);
        }
        else {
            let arr = editLookUpValues;
            if (arr && ArrayExtensions_1.default.IsNotNullOrEmpty(arr)) {
                return arr;
            }
        }
        // if no hard-coded values or function provided then just get the distinct values for the column
        // this will use the columnApi method that first looks for permitted values and then distinct values
        return this.getDistinctDisplayValuesForColumn(column.columnId);
    }
}
exports.GridInternalApi = GridInternalApi;
